import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { translate, readLang } from "../i18n/kt";
import "./karkhana-ui.scss";

/**
 * In-app confirm / "say why" dialogs, instead of the browser's confirm() and prompt() — those are
 * blocked in some browsers and in-app views, look foreign, and can't be translated. One host is
 * mounted for the whole app; callers just `await confirmDialog(...)` or `await askReason(...)`.
 */
type Request =
  | { kind: "confirm"; title: string; body?: string; ok?: string; danger?: boolean; resolve: (v: boolean) => void }
  | {
      kind: "text";
      title: string;
      body?: string;
      ok?: string;
      placeholder?: string;
      /** Ready reasons in a dropdown (saved in English, shown translated); "Other" opens the text box. */
      choices?: string[];
      resolve: (v: string | null) => void;
    };

const OTHER = "Other";

let show: ((r: Request) => void) | null = null;

export const confirmDialog = (o: { title: string; body?: string; ok?: string; danger?: boolean }): Promise<boolean> =>
  new Promise((resolve) => {
    if (!show) return resolve(window.confirm(o.title)); // no host mounted (tests): plain confirm
    show({ kind: "confirm", ...o, resolve });
  });

/** Ask for a short reason (removing or rejecting something). Resolves null when cancelled. */
export const askReason = (o: { title: string; body?: string; ok?: string; placeholder?: string; choices?: string[] }): Promise<string | null> =>
  new Promise((resolve) => {
    if (!show) return resolve(window.prompt(o.title));
    show({ kind: "text", ...o, resolve });
  });

export const DialogHost = () => {
  const [req, setReq] = useState<Request | null>(null);
  const [text, setText] = useState("");
  const [choice, setChoice] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);
  const t = (s: string) => translate(readLang(), s);

  useEffect(() => {
    show = (r) => {
      setText("");
      setChoice("");
      setReq(r);
    };
    return () => {
      show = null;
    };
  }, []);

  const close = (ok: boolean) => {
    if (!req) return;
    if (req.kind === "confirm") req.resolve(ok);
    else req.resolve(ok ? text.trim() || null : null);
    setReq(null);
  };

  const needsText = req?.kind === "text";
  const choices = req?.kind === "text" ? req.choices : undefined;
  // With a dropdown, the text box shows only for "Other".
  const typing = needsText && (!choices || choice === OTHER);
  return (
    <Modal isOpen={!!req} toggle={() => close(false)} centered onOpened={() => input.current?.focus()} className="kd-dialog">
      {req ? (
        <>
          <ModalHeader toggle={() => close(false)}>{req.title}</ModalHeader>
          {req.body || needsText ? (
            <ModalBody>
              {req.body ? <p className="kd-dialog__body">{req.body}</p> : null}
              {choices ? (
                <select
                  className="kd-dialog__input"
                  value={choice}
                  aria-label={t("Reason")}
                  onChange={(e) => {
                    setChoice(e.target.value);
                    setText(e.target.value === OTHER ? "" : e.target.value);
                  }}
                >
                  <option value="" disabled>
                    {t("Choose a reason")}
                  </option>
                  {[...choices, OTHER].map((c) => (
                    <option key={c} value={c}>
                      {t(c)}
                    </option>
                  ))}
                </select>
              ) : null}
              {typing ? (
                <textarea
                  ref={input}
                  className="kd-dialog__input"
                  rows={2}
                  value={text}
                  placeholder={(req as any).placeholder || t("Reason")}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && text.trim()) {
                      e.preventDefault();
                      close(true);
                    }
                  }}
                />
              ) : null}
            </ModalBody>
          ) : null}
          <ModalFooter>
            <button type="button" className="kh-btn" onClick={() => close(false)}>
              {t("Cancel")}
            </button>
            <button
              type="button"
              className={`kh-btn is-primary${req.kind === "confirm" && req.danger ? " is-danger" : ""}`}
              disabled={needsText && !text.trim()}
              onClick={() => close(true)}
            >
              {req.ok || t(needsText ? "Done" : "Yes")}
            </button>
          </ModalFooter>
        </>
      ) : null}
    </Modal>
  );
};
